<script>
  import { onDestroy, onMount, tick } from "svelte";
  import {
    Checkbox,
    RadioButton,
    RadioButtonGroup,
  } from "carbon-components-svelte";
  import Button from "lib/components/Button.svelte";
  import Modal from "lib/components/Modal.svelte";
  import { Select, Tooltip } from "paxray-components-svelte";
  import { recordingStore } from "@stores/recordingStore.js";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  let saveToastMsg = "",
    saveSuccessful = false,
    preprocessingProjects = [],
    preprocessingProjectId = "";
  let preprocessingClean = false,
    preprocessingJob = { status: "idle" },
    preprocessingMessage = "";
  let preprocessingTimer,
    preprocessingWarningOpen = false,
    pendingPreprocessingStage = null;
  let refreshedPreprocessingJob = "",
    monitoringTransferOpen = false,
    monitoringProjectIds = [];
  let monitoringMode = "delta",
    monitoringTransferring = false,
    monitoringTransferError = "";
  // Die Aktivitaetsbenennung laeuft je Aufgabe und kostet Sprachmodell-Aufrufe.
  // Deshalb wird sie vor dem Start ausgewaehlt statt ueber das ganze Projekt zu
  // laufen.
  let preprocessingSubtasks = [],
    selectedSubtaskIds = [],
    subtasksLoading = false,
    subtasksError = "";

  const stagesNeedingSubtasks = ["activities", "activities_grouping"];
  $: needsSubtaskChoice = stagesNeedingSubtasks.includes(
    pendingPreprocessingStage?.key,
  );

  const preprocessingStages = [
    {
      key: "pipeline",
      label: "Task-recording preprocessing",
      description:
        "Runs the CLI-equivalent pipeline from anonymization through activity grouping.",
    },
    {
      key: "full",
      label: "Full task-recording pipeline",
      description:
        "Runs all stages in sequence, from anonymization through object processing.",
    },
    {
      key: "anonymization",
      label: "Anonymization",
      description:
        "Creates the anonymization preview for the recorded task data.",
    },
    {
      key: "preprocessing",
      label: "Preprocessing",
      description:
        "Transforms recordings into traces used by the analysis views.",
    },
    {
      key: "taxonomy_assignment",
      label: "Taxonomy assignment",
      description: "Assigns recorded tasks to the configured process taxonomy.",
    },
    {
      key: "outlier_detection",
      label: "Outlier detection",
      description:
        "Detects exceptional recordings that can be excluded from analyses.",
    },
    {
      key: "activities",
      label: "Activities",
      description:
        "Generates analysis activities from the preprocessed recording traces.",
    },
    {
      key: "caseid_processing",
      label: "Object processing",
      description: "Identifies and processes objects found in the recordings.",
    },
    {
      key: "analysis_caches",
      label: "Precompute caches",
      description:
        "Rebuilds the shared Global JSON snapshots and warms KPI Matrix, Treemap, App Flow, task selection, and Process Explorer tasks.",
    },
  ];
  // Gruppierung fuer die Darstellung: komplette Laeufe, Einzelschritte in
  // Pipeline-Reihenfolge und der davon unabhaengige Cache-Aufbau.
  const pipelineRunSteps = { pipeline: "Steps 1 to 5", full: "Steps 1 to 6" };
  const pipelineRuns = preprocessingStages.filter(
    (stage) => stage.key in pipelineRunSteps,
  );
  const singleSteps = preprocessingStages.filter(
    (stage) =>
      !(stage.key in pipelineRunSteps) && stage.key !== "analysis_caches",
  );
  const cacheStage = preprocessingStages.find(
    (stage) => stage.key === "analysis_caches",
  );
  let preprocessingMode = "new";
  $: preprocessingClean = preprocessingMode === "clean";
  $: isRunning = preprocessingJob.status === "running";
  $: runDisabled = !preprocessingProjectId || isRunning;
  const preprocessingResultLabels = {
    red: "Flagged red",
    yellow: "Flagged yellow",
    unique_activities: "Unique activities",
    unassigned_events: "Unassigned events",
    entries: "Object entries",
    assigned: "Assigned",
    newly_assigned: "Newly assigned",
    unassigned: "Unassigned",
    anonymized_events: "Anonymized events",
    replacements: "Replacements",
  };

  function preprocessingProjectKey(project) {
    return String(
      project?.id || project?.project_id || project?.idproject || "",
    );
  }

  onMount(async () => {
    await Promise.all([loadPreprocessingProjects(), loadPreprocessingStatus()]);
    preprocessingTimer = setInterval(loadPreprocessingStatus, 3000);
  });
  onDestroy(() => clearInterval(preprocessingTimer));

  async function loadPreprocessingProjects() {
    const response = await fetchWithSession("/api/damprojects/projects");
    if (!response.ok) return;
    const data = await response.json();
    preprocessingProjects = [...(data.analysis || []), ...(data.running || [])];
    preprocessingProjectId ||= preprocessingProjectKey(
      preprocessingProjects[0],
    );
  }
  async function loadPreprocessingStatus() {
    const response = await fetchWithSession(
      "/api/damprojects/admin/preprocessing",
    );
    if (!response.ok) return;
    const nextJob = await response.json();
    preprocessingJob = nextJob;
    const refreshKey =
      String(nextJob.project_id || "") +
      ":" +
      String(nextJob.finished_at || "");
    if (
      nextJob.status === "completed" &&
      ["outlier_detection", "pipeline", "full"].includes(nextJob.stage) &&
      nextJob.project_id &&
      refreshKey !== refreshedPreprocessingJob
    ) {
      refreshedPreprocessingJob = refreshKey;
      await recordingStore.getRecordings(nextJob.project_id);
    }
  }
  function preprocessingStageLabel(stageKey) {
    return (
      preprocessingStages.find((stage) => stage.key === stageKey)?.label ||
      { taskname: "Task naming", activities_grouping: "Activity grouping" }[
        stageKey
      ] ||
      stageKey ||
      "Preparing"
    );
  }
  function preprocessingResultLabel(key) {
    return preprocessingResultLabels[key] || key.replaceAll("_", " ");
  }
  function anonymizationResultCount(result, ...keys) {
    return keys
      .reduce((total, key) => total + Number(result[key] || 0), 0)
      .toLocaleString();
  }
  async function loadSubtasks() {
    subtasksLoading = true;
    subtasksError = "";
    preprocessingSubtasks = [];
    selectedSubtaskIds = [];
    try {
      const response = await fetchWithSession(
        "/api/damprojects/admin/preprocessing/subtasks?project_id=" +
          encodeURIComponent(preprocessingProjectId),
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Tasks unavailable");
      preprocessingSubtasks = data.subtasks || [];
      // Standard: alle. Wer einschraenken will, haekelt ab.
      selectedSubtaskIds = preprocessingSubtasks.map((task) => task.id);
    } catch (error) {
      subtasksError = "The tasks of this project could not be loaded.";
    } finally {
      subtasksLoading = false;
    }
  }
  function toggleSubtask(id) {
    selectedSubtaskIds = selectedSubtaskIds.includes(id)
      ? selectedSubtaskIds.filter((item) => item !== id)
      : [...selectedSubtaskIds, id];
  }
  function requestPreprocessing(stage) {
    pendingPreprocessingStage = stage;
    preprocessingWarningOpen = true;
    if (stagesNeedingSubtasks.includes(stage?.key)) loadSubtasks();
  }
  async function runPreprocessing() {
    const stage = pendingPreprocessingStage?.key;
    if (!preprocessingProjectId || preprocessingJob.status === "running")
      return;
    if (needsSubtaskChoice && !selectedSubtaskIds.length) return;
    const project =
      preprocessingProjects.find(
        (item) =>
          preprocessingProjectKey(item) === String(preprocessingProjectId),
      ) || preprocessingProjects[0];
    preprocessingWarningOpen = false;
    preprocessingMessage = "";
    const response = await fetchWithSession(
      "/api/damprojects/admin/preprocessing",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: preprocessingProjectKey(project),
          project_name: project?.name,
          stage,
          clean: preprocessingClean,
          ...(needsSubtaskChoice &&
          selectedSubtaskIds.length &&
          selectedSubtaskIds.length < preprocessingSubtasks.length
            ? { subtask_ids: selectedSubtaskIds }
            : {}),
        }),
      },
    );
    const data = await response.json();
    if (!response.ok) {
      preprocessingMessage =
        data.message || "Preprocessing could not be started.";
      return;
    }
    preprocessingJob = data;
    pendingPreprocessingStage = null;
  }
  function toggleMonitoringProject(projectId) {
    monitoringProjectIds = monitoringProjectIds.includes(projectId)
      ? monitoringProjectIds.filter((id) => id !== projectId)
      : [...monitoringProjectIds, projectId];
  }
  function monitoringFailureMessage(data) {
    const failure = data.failed?.[0],
      message =
        failure?.message || data.message || "Monitoring transfer failed.";
    if (
      failure?.status === 409 ||
      message.toLowerCase().includes("precompute caches")
    ) {
      const project = preprocessingProjects.find(
        ({ id }) => String(id) === String(failure?.project_id),
      );
      return (
        "Calculated Software Catalog results are not prepared for " +
        (project?.name || "the selected project") +
        '. Run "Precompute caches" under Preprocessing Operations, then retry the transfer.'
      );
    }
    return message;
  }
  async function transferMonitoringResults() {
    if (!monitoringProjectIds.length || monitoringTransferring) return;
    monitoringTransferring = true;
    monitoringTransferError = "";
    saveToastMsg = "";
    const startedAt = Date.now();
    await tick();
    try {
      const response = await fetchWithSession(
        "/api/monitoring/admin/transfers",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_ids: monitoringProjectIds,
            mode: monitoringMode,
          }),
        },
      );
      const data = await response.json();
      if (!response.ok) throw new Error(monitoringFailureMessage(data));
      const failed = data.failed?.length || 0;
      await waitForVisibleTransferState(startedAt);
      saveSuccessful = failed === 0;
      if (!failed) {
        saveToastMsg = [
          (data.imported?.length || 0) + " project results transferred",
          (data.skipped?.length || 0) + " unchanged",
        ].join(" · ");
        monitoringTransferOpen = false;
        monitoringProjectIds = [];
      } else monitoringTransferError = monitoringFailureMessage(data);
    } catch (error) {
      await waitForVisibleTransferState(startedAt);
      saveSuccessful = false;
      monitoringTransferError = error.message || "Monitoring transfer failed.";
    } finally {
      monitoringTransferring = false;
    }
  }
  async function waitForVisibleTransferState(startedAt) {
    const remaining = 700 - (Date.now() - startedAt);
    if (remaining > 0)
      await new Promise((resolve) => setTimeout(resolve, remaining));
  }
  function closePreprocessingWarning() {
    preprocessingWarningOpen = false;
    pendingPreprocessingStage = null;
  }
</script>

<div class="management-section">
  <div class="table_heading">
    <h5>Preprocessing</h5>
    <div>
      Prepare the recordings of a project for analysis. Pick a project, then
      run all steps at once or a single step.
    </div>
  </div>

  <section
    class:running={isRunning}
    class:failed={preprocessingJob.status === "failed"}
    class:completed={preprocessingJob.status === "completed"}
    class="run-status"
    aria-live="polite"
  >
    <div class="run-status-heading">
      <span class="status-dot"></span>
      <strong
        >{#if isRunning}Running{:else if preprocessingJob.status === "completed"}Last
          run completed{:else if preprocessingJob.status === "failed"}Last run
          failed{:else}Nothing is running{/if}</strong
      >
      {#if preprocessingJob.stage && preprocessingJob.status !== "idle"}<span
          class="run-status-stage"
          >{preprocessingStageLabel(preprocessingJob.stage)}</span
        >{/if}
    </div>
    {#if preprocessingJob.status === "idle" || !preprocessingJob.status}<small
        >Start an operation below. Its progress will appear here.</small
      >{:else}<div class="operation-progress">
        <div class="progress-heading">
          <span
            >{preprocessingStageLabel(
              preprocessingJob.current_stage || preprocessingJob.stage,
            )}</span
          ><strong>{preprocessingJob.progress || 0}%</strong>
        </div>
        <div
          class="progress-track"
          role="progressbar"
          aria-label="Preprocessing progress"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={preprocessingJob.progress || 0}
        >
          <div
            class="progress-value"
            style={"width: " + (preprocessingJob.progress || 0) + "%"}
          ></div>
        </div>
        <small
          >{#if preprocessingJob.total_items != null}{preprocessingJob.completed_items ||
              0} of {preprocessingJob.total_items}
            {preprocessingJob.progress_unit || "items"} completed{:else}{preprocessingJob.completed_steps ||
              0} of {preprocessingJob.total_steps || 1} steps completed{/if}</small
        >{#if Object.keys(preprocessingJob.results || {}).length}<div
            class="operation-results"
          >
            {#each Object.entries(preprocessingJob.results) as [stage, result]}<div
              >
                {#if stage === "anonymization"}<strong
                    >{anonymizationResultCount(result, "rows_written")} masks updated</strong
                  ><span>·</span><strong
                    >{anonymizationResultCount(
                      result,
                      "ui_element_rows_inserted",
                      "ui_element_rows_updated",
                    )} elements updated</strong
                  >{:else}<strong>{preprocessingStageLabel(stage)}:</strong
                  >{#each Object.entries(result) as [key, value], index}{#if index > 0}<span
                        >·</span
                      >{/if}{preprocessingResultLabel(key)}
                    <strong>{value}</strong>{/each}{/if}
              </div>{/each}
          </div>{/if}
      </div>{/if}{#if preprocessingJob.error}<p class="operation-error">
        {preprocessingJob.error}
      </p>{/if}{#if preprocessingMessage}<p class="operation-error">
        {preprocessingMessage}
      </p>{/if}
  </section>

  <section class="preprocessing-operations">
    <div class="preprocessing-controls">
      <Select
        labelText="Project"
        items={preprocessingProjects.map((project) => ({
          value: preprocessingProjectKey(project),
          text: project.name,
        }))}
        bind:selected={preprocessingProjectId}
        disabled={isRunning}
      />
      <Select
        items={[
          { value: "new", text: "New recordings" },
          { value: "clean", text: "Clean run" },
        ]}
        bind:selected={preprocessingMode}
        disabled={isRunning}
        ><span slot="labelText" class="select-label"
          >Run mode<Tooltip
            text={"New recordings: only processes recordings added since the last run.\nClean run: reprocesses all recordings of the project."}
          /></span
        ></Select
      >
    </div>

    <div class="stage-group">
      <div class="stage-heading">Run all steps</div>
      {#each pipelineRuns as stage}<div
          class="stage-row"
          class:active={isRunning && preprocessingJob.stage === stage.key}
        >
          <span class="stage-badge">{pipelineRunSteps[stage.key]}</span>
          <div class="stage-text">
            <strong>{stage.label}</strong><small>{stage.description}</small>
          </div>
          <Button
            text="Run"
            size="sm"
            variant="primary"
            disabled={runDisabled}
            on:click={() => requestPreprocessing(stage)}
          />
        </div>{/each}
    </div>

    <div class="stage-group">
      <div class="stage-heading">Run a single step</div>
      {#each singleSteps as stage, index}<div
          class="stage-row"
          class:active={isRunning &&
            (preprocessingJob.current_stage || preprocessingJob.stage) ===
              stage.key}
        >
          <span class="stage-number">{index + 1}</span>
          <div class="stage-text">
            <strong>{stage.label}</strong><small>{stage.description}</small>
          </div>
          <Button
            text="Run"
            size="sm"
            variant="secondary"
            disabled={runDisabled}
            on:click={() => requestPreprocessing(stage)}
          />
        </div>{/each}
    </div>

    <div class="stage-group">
      <div class="stage-heading">After preprocessing</div>
      <div
        class="stage-row"
        class:active={isRunning && preprocessingJob.stage === cacheStage.key}
      >
        <span class="stage-badge">Caches</span>
        <div class="stage-text">
          <strong>{cacheStage.label}</strong><small
            >{cacheStage.description} Needed before results can be transferred
            to monitoring.</small
          >
        </div>
        <Button
          text="Run"
          size="sm"
          variant="secondary"
          disabled={runDisabled}
          on:click={() => requestPreprocessing(cacheStage)}
        />
      </div>
    </div>
  </section>
</div>

<div class="management-section">
  <div class="table_heading">
    <h5>Monitoring Data Transfer</h5>
    <div>
      Transfer calculated software results from selected projects into the
      separate monitoring database.
    </div>
  </div>
  <section class="monitoring-transfer">
    <div>
      <strong>Software Intelligence</strong>
      <small>
        Raw recording data stays in the project services. Only calculated result
        snapshots are transferred.
      </small>
    </div>
    <Button
      text="Transfer results"
      variant="secondary"
      disabled={!preprocessingProjects.length}
      on:click={() => {
        monitoringTransferError = "";
        monitoringTransferOpen = true;
      }}
    />
  </section>
</div>

<Modal
  actions
  autowidth
  modalClass="preprocessing-warning-modal"
  preventCloseOnClickOutside
  bind:open={preprocessingWarningOpen}
  modalHeading="Run preprocessing operation?"
  primaryButtonText="Run operation"
  secondaryButtonText="Cancel"
  primaryButtonAction={runPreprocessing}
  secondaryButtonAction={closePreprocessingWarning}
  on:close={() => {
    preprocessingWarningOpen = false;
  }}
  ><div class="preprocessing-warning">
    <div class="warning-icon">!</div>
    <div>
      <strong>This operation can overwrite calculated analysis data.</strong>
      <p>
        {pendingPreprocessingStage?.key === "analysis_caches"
          ? "Existing analysis caches will be cleared and rebuilt. Source data is not changed."
          : "Please confirm the selected configuration before continuing."}
      </p>
    </div>
  </div>
  {#if needsSubtaskChoice}
    <div class="subtask-choice">
      <div class="subtask-heading">
        <strong>Which tasks should be relabelled?</strong>
        <small
          >Naming runs per task. Unticked tasks keep the activity names they
          already have.</small
        >
      </div>
      {#if subtasksLoading}
        <p class="subtask-note">Loading tasks…</p>
      {:else if subtasksError}
        <p class="subtask-note subtask-error">{subtasksError}</p>
      {:else if !preprocessingSubtasks.length}
        <p class="subtask-note">
          This project has no task with recordings yet.
        </p>
      {:else}
        <div class="subtask-actions">
          <button
            type="button"
            on:click={() =>
              (selectedSubtaskIds = preprocessingSubtasks.map((t) => t.id))}
            >Select all</button
          >
          <button type="button" on:click={() => (selectedSubtaskIds = [])}
            >Clear</button
          >
        </div>
        <ul class="subtask-list">
          {#each preprocessingSubtasks as task}
            <li>
              <label>
                <input
                  type="checkbox"
                  checked={selectedSubtaskIds.includes(task.id)}
                  on:change={() => toggleSubtask(task.id)}
                />
                <span class="subtask-name">{task.name}</span>
                <span class="subtask-count"
                  >{task.recordings} recording{task.recordings === 1
                    ? ""
                    : "s"}{task.labelled
                    ? ` · ${task.labelled} already labelled`
                    : ""}</span
                >
              </label>
            </li>
          {/each}
        </ul>
        {#if !selectedSubtaskIds.length}
          <p class="subtask-note subtask-error">
            Pick at least one task, or the run has nothing to do.
          </p>
        {/if}
      {/if}
    </div>
  {/if}
  <dl class="operation-summary">
    <div>
      <dt>Project</dt>
      <dd>
        {preprocessingProjects.find(
          (project) => String(project.id) === String(preprocessingProjectId),
        )?.name || "–"}
      </dd>
    </div>
    <div>
      <dt>Operation</dt>
      <dd>{pendingPreprocessingStage?.label || "–"}</dd>
    </div>
    <div>
      <dt>Mode</dt>
      <dd>
        {pendingPreprocessingStage?.key === "analysis_caches"
          ? "Full rebuild – caches only"
          : preprocessingClean
            ? "Clean run – reprocess all recordings"
            : "Incremental run – only new recordings"}
      </dd>
    </div>
  </dl></Modal
>

<Modal
  actions
  preventCloseOnClickOutside
  bind:open={monitoringTransferOpen}
  autowidth
  modalClass="monitoring-transfer-modal"
  modalHeading="Transfer results to monitoring"
  primaryButtonText={monitoringTransferring
    ? "Transferring..."
    : "Transfer results"}
  secondaryButtonText="Cancel"
  noSecondaryButton={monitoringTransferring}
  primaryButtonValidations={!monitoringProjectIds.length ||
    monitoringTransferring}
  primaryButtonAction={transferMonitoringResults}
  secondaryButtonAction={() => {
    monitoringTransferOpen = false;
    monitoringTransferError = "";
  }}
  on:close={() => {
    monitoringTransferOpen = false;
    monitoringTransferError = "";
  }}
  ><div class="monitoring-modal">
    {#if monitoringTransferring}<div
        class="monitoring-progress"
        role="status"
        aria-live="polite"
      >
        <span class="monitoring-spinner" aria-hidden="true"></span><strong
          >Transferring calculated results</strong
        >
        <p>
          The selected project snapshots are being written to the monitoring
          database.
        </p>
      </div>{:else}<div class="monitoring-field">
        <h4>Projects</h4>
        <div class="monitoring-projects">
          {#each preprocessingProjects as project}<div
              class="monitoring-project-row"
            >
              <Checkbox
                id={"monitoring-project-" + project.id}
                labelText={project.name}
                checked={monitoringProjectIds.includes(project.id)}
                on:change={() => toggleMonitoringProject(project.id)}
              /><small>{project.status}</small>
            </div>{/each}
        </div>
      </div>
      <div class="monitoring-field">
        <h4>Transfer mode</h4>
        <div class="monitoring-modes">
          <RadioButtonGroup
            legendText=""
            name="monitoring-mode"
            orientation="vertical"
            bind:selected={monitoringMode}
            ><div class="monitoring-mode-row">
              <RadioButton
                id="monitoring-mode-delta"
                labelText="Delta"
                value="delta"
              /><small
                >Only add a snapshot when calculated results changed.</small
              >
            </div>
            <div class="monitoring-mode-row">
              <RadioButton
                id="monitoring-mode-full"
                labelText="Full replacement"
                value="full"
              /><small
                >Replace stored snapshots for the selected projects.</small
              >
            </div></RadioButtonGroup
          >
        </div>
      </div>
      {#if monitoringTransferError}<p class="operation-error">
          {monitoringTransferError}
        </p>{/if}{/if}
  </div></Modal
>

<style>
  .subtask-choice {
    margin: 16px 0 8px;
    padding: 14px 16px;
    border: 1px solid var(--cds-ui-04, #8d8d8d);
    border-radius: 4px;
  }
  .subtask-heading small {
    display: block;
    margin-top: 2px;
    opacity: 0.75;
  }
  .subtask-actions {
    display: flex;
    gap: var(--control-gap);
    margin: 10px 0 6px;
  }
  .subtask-actions button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-decoration: underline;
    font: inherit;
    color: inherit;
  }
  .subtask-list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 220px;
    overflow-y: auto;
    /* Untereinander, nicht nebeneinander: die Liste erbt sonst die
       Reihenanordnung des umgebenden Abschnitts. */
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .subtask-list label {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-areas: "box name" ". count";
    gap: 2px 10px;
    align-items: center;
    cursor: pointer;
  }
  .subtask-list input {
    grid-area: box;
  }
  .subtask-name {
    grid-area: name;
  }
  .subtask-count {
    grid-area: count;
    font-size: 12px;
    opacity: 0.7;
  }
  .subtask-note {
    margin: 8px 0 0;
    font-size: 13px;
  }
  .subtask-error {
    color: var(--cds-support-01, #da1e28);
  }

  .management-section {
    margin-top: 0.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  section {
    border: 1px solid var(--border-color);
    border-radius: 0.5rem;
    padding: 1rem;
  }

  section.preprocessing-operations {
    border: none;
    padding: 0;
  }
  .select-label {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }
  .preprocessing-controls {
    display: grid;
    grid-template-columns: minmax(16rem, 1fr) minmax(16rem, 1fr);
    gap: var(--control-gap);
    /* Unten buendig, da das Tooltip-Icon das Run-mode-Label etwas hoeher macht. */
    align-items: end;
  }
  .monitoring-field,
  .operation-progress {
    background: var(--pax-white);
    border: 1px solid var(--border-color);
    border-radius: 0.5rem;
    padding: 0.85rem 1rem;
  }
  small {
    display: block;
    margin-top: 0.2rem;
    color: #6f7681;
    line-height: 1.3;
  }
  .stage-group {
    display: flex;
    flex-direction: column;
    margin-top: 1.25rem;
  }
  .stage-heading {
    margin-bottom: 0.4rem;
    color: var(--pax-black);
    font-size: 0.85rem;
    font-weight: 600;
  }
  .stage-row {
    display: grid;
    grid-template-columns: 6rem minmax(0, 1fr) auto;
    align-items: center;
    gap: 1rem;
    padding: 0.6rem 0.75rem;
    border-top: 1px solid var(--border-color);
  }
  .stage-heading + .stage-row {
    border-top: none;
  }
  .stage-row.active {
    border-radius: 0.4rem;
    background: #fff0e6;
  }
  .stage-text {
    min-width: 0;
  }
  .stage-text strong {
    font-size: 0.875rem;
  }
  .stage-number {
    display: grid;
    width: 1.5rem;
    height: 1.5rem;
    place-items: center;
    border-radius: 50%;
    background: var(--pax-smoke);
    font-size: 0.75rem;
    font-weight: 600;
  }
  .stage-badge {
    width: fit-content;
    padding: 0.2rem 0.5rem;
    border-radius: 1rem;
    background: var(--pax-smoke);
    font-size: 0.75rem;
    white-space: nowrap;
  }
  .run-status-heading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .run-status-stage {
    color: #525252;
  }
  .run-status.running {
    border-color: var(--pax-orange);
  }
  .run-status.failed {
    border-color: #da1e28;
  }
  .monitoring-transfer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
  .status-dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: #8d8d8d;
  }
  .run-status.running .status-dot {
    background: var(--pax-orange);
  }
  .run-status.completed .status-dot {
    background: #198038;
  }
  .run-status.failed .status-dot {
    background: #da1e28;
  }
  .operation-progress {
    margin-top: 0.75rem;
  }
  .progress-heading {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    font-size: 0.85rem;
  }
  .progress-track {
    height: 0.5rem;
    margin-top: 0.55rem;
    overflow: hidden;
    border-radius: 1rem;
    background: #e0e0e0;
  }
  .progress-value {
    height: 100%;
    border-radius: inherit;
    background: var(--pax-orange);
    transition: width 0.3s ease;
  }
  .operation-results {
    margin-top: 0.75rem;
  }
  .operation-error {
    color: #da1e28;
  }
  .monitoring-modal {
    display: grid;
    gap: 1rem;
    width: 100%;
  }
  .monitoring-field {
    min-width: 0;
  }
  .monitoring-field h4 {
    margin: 0 0 0.75rem;
    color: var(--pax-black);
    font-size: 0.85rem;
  }
  .monitoring-projects {
    display: grid;
    max-height: 12rem;
    gap: 0.4rem;
    overflow-y: auto;
  }
  .monitoring-project-row,
  .monitoring-mode-row {
    display: grid;
    gap: 0.2rem;
    padding: 0.6rem 0.7rem;
    border-radius: 0.35rem;
  }
  .monitoring-project-row:hover,
  .monitoring-mode-row:hover {
    background: var(--pax-smoke);
  }
  .monitoring-project-row small,
  .monitoring-mode-row small {
    padding-left: 1.75rem;
  }
  .monitoring-modes :global(.bx--radio-button-group) {
    display: grid;
    gap: 0.35rem;
  }
  .monitoring-progress {
    display: grid;
    min-height: 13rem;
    place-items: center;
    align-content: center;
    gap: 0.65rem;
    padding: 1.5rem;
    text-align: center;
  }
  .monitoring-progress p {
    max-width: 24rem;
    margin: 0;
  }
  .monitoring-spinner {
    width: 2rem;
    height: 2rem;
    border: 0.2rem solid #e0e0e0;
    border-top-color: var(--pax-orange);
    border-radius: 50%;
    animation: monitoring-spin 0.8s linear infinite;
  }
  .preprocessing-warning {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
  }
  .preprocessing-warning p {
    margin: 0.25rem 0 0;
    color: #525252;
    line-height: 1.4;
  }
  .warning-icon {
    display: grid;
    flex: 0 0 2rem;
    width: 2rem;
    height: 2rem;
    place-items: center;
    border-radius: 50%;
    background: #fff0e6;
    color: var(--pax-orange);
    font-weight: 700;
  }
  .operation-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
    gap: 0.65rem;
    margin: 1rem 0 0;
  }
  .operation-summary > div {
    padding: 0.7rem 0.8rem;
    border: 1px solid var(--border-color);
    border-radius: 0.4rem;
    background: #fafafa;
  }
  .operation-summary dt {
    color: #6f7681;
    font-size: 0.75rem;
  }
  .operation-summary dd {
    margin: 0.2rem 0 0;
    color: var(--pax-black);
    font-weight: 600;
  }
  :global(.preprocessing-warning-modal .bx--modal-container) {
    width: min(38rem, calc(100vw - 2rem));
    max-width: 38rem;
    max-height: calc(100vh - 2rem);
  }
  :global(.monitoring-transfer-modal .bx--modal-container) {
    width: min(34rem, calc(100vw - 2rem));
    max-width: 34rem;
    max-height: calc(100vh - 2rem);
  }
  @keyframes monitoring-spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
